import type { RelevanceTier } from "../lib/skill-matcher/relevance-tier";
import type { FilterOptionTreeNode } from "./filtermenu";
import type { GeoBoundaries, GeoLocation, RadiusOption } from "./geo";
import type { JobExcerpt, JobMapPin } from "./job";
import type { BranchMapPin } from "./leaflet";
import type { SuggestedFilter } from "./skill-matcher";

export const filterTypes = {
  BUSINESS_UNIT: "businessUnit",
  CATEGORY: "category",
  COMPANY: "company",
  CONTRACT: "contract",
  EMPLOYMENT_TYPE: "employmentType",
  CLASSIFICATION: "classification",
  ENTRY_LEVEL: "entryLevel",
  ENTRY_LEVEL_MULTI: "entryLevel_multi",
  JOB_FIELD: "jobField",
  LANGUAGE: "language",
  FILTER_LANGUAGE: "filterLanguage",
  REMOTE: "remote",
  SUB_CLIENTS: "subClients",
  LOCATION_NAME: "locations.name",
  LOCATION_CITY: "locations.city",
  LOCATION_CITY_STATE: "locations.cityState",
  LOCATION_STATE: "locations.state",
  LOCATION_COUNTRY: "locations.country",
  LOCATION_CONTINENT: "locations.continent",
  DATA_SOURCE: "data_source",
} as const;

export type Filter = (typeof filterTypes)[keyof typeof filterTypes];

const locationFilterTypes = [
  filterTypes.LOCATION_CITY_STATE,
  filterTypes.LOCATION_STATE,
  filterTypes.LOCATION_COUNTRY,
  filterTypes.LOCATION_CONTINENT,
] as const;

type LocationFilter = (typeof locationFilterTypes)[number];

export const isLocationFilter = (
  filterType: unknown
): filterType is LocationFilter =>
  typeof filterType === "string" &&
  locationFilterTypes.includes(filterType as LocationFilter);

export type FilterFacet_Legacy = `data.${Filter}`;

export type FacetCounts = Record<string, number>;

export type FacetsCounts = {
  [filterBy in FilterFacet_Legacy]?: FacetCounts;
};

export type FacetOrders = Record<string, number>;

export type FacetsOrders = {
  [filterBy in FilterFacet_Legacy]?: FacetOrders;
};

export interface PostingDateCount {
  count: number;
  label: string;
  value: string;
}

export interface FilterInfo {
  branches: BranchMapPin[];
  facetsCounts: FacetsCounts;
  facetsOrders: FacetsOrders;
  locationTree: FilterOptionTreeNode[];
  postingDateCounts: PostingDateCount[];
}

export interface FilterSearchParams {
  boundaries?: GeoBoundaries;
  filter: Filter_Legacy;
  geoLocation?: GeoLocation;
  /** Overrides default page size (e.g. from URL `pageSize` on embedded widgets). */
  pageSize?: number;
  postingDateFilter?: string;
  radius?: RadiusOption;
  searchQuery: string;
  subclient?: string;
}

export interface FilterResultPage {
  /** A3.8 v2: the coverage-PENALTY ranking arm ACTUALLY fed this page's
   *  order (a consented profile plus at least one posting carrying anything
   *  `penaltyApplies` reads — mustHave, certifications, licenses or
   *  education) — not merely that the experiment flag was set. Present only
   *  when true. The transparency popover's coverage-penalty disclosure is
   *  gated on it, so the note is never shown on a page it did not move, and
   *  never hidden on a page it did. Same derivation as the audit row's
   *  `layerFlags.coverageRanking`. */
  coverageRanking?: boolean;
  duration?: number;
  facetsCounts: FacetsCounts;
  jobs: JobExcerpt[];
  jobsPerPage: number;
  nextPage: number | null;
  page: number;
  previousPage: number | null;
  /** Design 2026-09-14: the exact strings the server embedded and reranked
   *  for this run, echoed so the UI shows what was used instead of
   *  recomputing it. `rewritten` = a model pass ran; `fallback` = wording
   *  was deleted deterministically. Present only on skill-matcher
   *  responses. */
  searchTextUsed?: {
    fallback: boolean;
    rerankInstruction: string;
    rewritten: boolean;
    searchQuestion: string;
  };
  /** A3.6: the search question carried too little to search on, so the
   *  relevance floor was widened for this run. Present only when true; the
   *  UI turns it into a visible notice, because A3.6 requires the widening
   *  to be stated rather than silent. */
  sparseMode?: boolean;
  /** F3: present only on page-0 skill-matcher responses for consented
   *  sessions with at least one surviving suggestion (A5.1–A5.3). */
  suggestedFilters?: SuggestedFilter[];
  /** A3.2: how many results on this page sit at the highest qualitative
   *  tier, and — when fewer than three do — the tier the requirement was
   *  loosened to, so the UI can mark it. Present only on skill-matcher
   *  responses (page 0 carries the meaningful value). */
  tierRelaxedTo?: RelevanceTier;
  topTierCount?: number;
  totalHits: number;
}

export interface FilterMapPinResultPage {
  jobMapPins: JobMapPin[];
  jobsPerPage: number;
  nextPage: number | null;
  page: number;
  previousPage: number | null;
  totalHits: number;
}

export type FilterValue = `${Filter}:${string}`;

export type SelectedFilters = {
  [filterBy in Filter]?: FilterValue[];
};

export type FilterValueItem_Legacy = `${FilterFacet_Legacy}:${string}`;

export type Filter_Legacy = {
  [filterBy in FilterFacet_Legacy]?: FilterValueItem_Legacy[];
};
